import React, { useState, useEffect } from 'react';

const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastVersionSeen, setLastVersionSeen] = useState(null);

  useEffect(() => {
    // Escuchar mensajes del Service Worker
    const handleSWMessage = (event) => {
      if (event.data && event.data.type === 'SW_UPDATE_AVAILABLE') {
        const { version } = event.data;
        console.log('🔄 Nueva versión de la app disponible:', version);
        
        // Solo mostrar si es realmente una versión nueva que no hemos visto
        if (version && version !== lastVersionSeen) {
          setLastVersionSeen(version);
          setShowUpdate(true);
        }
      }
      
      if (event.data && event.data.type === 'SW_UPDATED') {
        console.log('✅ Service Worker actualizado correctamente');
        setIsUpdating(false);
        setShowUpdate(false);
      }
    };

    // Registrar listener para mensajes del SW
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      
      // Obtener versión actual al inicializar
      getServiceWorkerVersion();
    }

    // Verificar actualizaciones periódicamente (cada 10 minutos en lugar de 5)
    const checkForUpdates = () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CHECK_FOR_UPDATES'
        });
      }
    };

    // Verificar al cargar y luego periódicamente
    checkForUpdates();
    const interval = setInterval(checkForUpdates, 10 * 60 * 1000); // 10 minutos

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
      clearInterval(interval);
    };
  }, [lastVersionSeen]);

  // Obtener la versión actual del Service Worker
  const getServiceWorkerVersion = async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        const messageChannel = new MessageChannel();
        
        messageChannel.port1.onmessage = (event) => {
          if (event.data && event.data.version) {
            setLastVersionSeen(event.data.version);
            console.log('📋 Versión actual del SW:', event.data.version);
          }
        };

        navigator.serviceWorker.controller.postMessage(
          { type: 'GET_VERSION' }, 
          [messageChannel.port2]
        );
      } catch (error) {
        console.error('Error obteniendo versión del SW:', error);
      }
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    try {
      console.log('🔄 Iniciando actualización de la aplicación...');
      
      // Intentar comunicarse con el SW para activar nueva versión
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        
        if (registration && registration.waiting) {
          // Hay un SW esperando, activarlo
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          
          // Esperar confirmación de activación
          const handleControllerChange = () => {
            console.log('🔄 Nuevo SW activado, recargando...');
            window.location.reload();
          };
          
          navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true });
          
          // Fallback: recargar después de 3 segundos si no hay respuesta
          setTimeout(() => {
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
            console.log('🔄 Recarga por timeout');
            window.location.reload();
          }, 3000);
          
        } else {
          // No hay SW esperando, solo recargar
          console.log('🔄 No hay SW esperando, recargando directamente');
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      } else {
        // Service Worker no disponible, recargar normalmente
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }

    } catch (error) {
      console.error('Error en el proceso de actualización:', error);
      // En caso de error, recargar de todas formas después de un delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
    console.log('📅 Actualización pospuesta por el usuario');
    
    // Marcar esta versión como "vista" para no mostrarla nuevamente por un tiempo
    localStorage.setItem('agustinos_update_dismissed', lastVersionSeen || Date.now().toString());
  };

  // No mostrar si ya se descartó recientemente esta versión
  useEffect(() => {
    const dismissedVersion = localStorage.getItem('agustinos_update_dismissed');
    if (dismissedVersion && dismissedVersion === lastVersionSeen) {
      setShowUpdate(false);
    }
  }, [lastVersionSeen]);

  if (!showUpdate) return null;

  return (
    <div 
      className="fixed left-1/2 transform -translate-x-1/2 z-50 animate-slide-down"
      style={{ 
        top: 'max(env(safe-area-inset-top, 20px) + 80px, 100px)', // Posicionar debajo del header
        maxWidth: '90vw', 
        width: '400px' 
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4">
        <div className="flex items-center gap-3">
          {/* Icono de actualización */}
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
            {isUpdating ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <span className="text-white font-bold text-lg">🔄</span>
            )}
          </div>

          {/* Contenido */}
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-sm">
              {isUpdating ? 'Actualizando...' : 'Nueva versión disponible'}
            </h3>
            <p className="text-gray-600 text-xs mt-1">
              {isUpdating 
                ? 'Aplicando actualización, por favor espera...' 
                : 'Hay mejoras y correcciones disponibles'
              }
            </p>
            {lastVersionSeen && !isUpdating && (
              <p className="text-gray-400 text-xs mt-1">
                Versión: {lastVersionSeen.substring(0, 8)}...
              </p>
            )}
          </div>

          {/* Botones */}
          {!isUpdating && (
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={handleDismiss}
                className="px-3 py-1 text-gray-500 hover:text-gray-700 text-xs font-medium transition-colors"
              >
                Después
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Actualizar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;